import React, { useRef, useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Button, Flex,
  Heading, Spacer, Box, IconButton, Text, Input, useToast,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure, Badge,
  InputGroup, InputLeftElement, Tooltip, VStack
} from "@chakra-ui/react";
import { 
  AddIcon, ArrowLeftIcon, ArrowRightIcon, DeleteIcon, 
  EditIcon, SearchIcon 
} from "@chakra-ui/icons";
import ApiResponse from "../services/ApiResponse";

interface TableCRUDProps {
  tableName: string;
  headers: string[];
  data: any[];
  infoColumn: string;
}

const TableCRUD: React.FC<TableCRUDProps> = ({
  tableName,
  headers,
  data,
  infoColumn,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [localData, setLocalData] = useState(data);
  const [itemToDelete, setItemToDelete] = useState<any>(null);

  const rowsPerPage = 8;

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const filterHeaders = useMemo(() => 
    headers.filter(h => !h.toLowerCase().includes("estado") && !h.toLowerCase().includes("id")),
    [headers]
  );

  const filteredData = useMemo(() => {
    const normalizedSearch = searchTerm.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    return localData.filter((item) =>
      Object.values(item).some(
        (value) =>
          typeof value === "string" &&
          value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(normalizedSearch)
      )
    );
  }, [localData, searchTerm]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

  const handleDelete = async () => {
    if (itemToDelete) {
      const apiResponse = new ApiResponse();
      await apiResponse.useFetch(`${tableName.toLowerCase()}/${itemToDelete.id}`, "DELETE");

      if (!apiResponse.error) {
        setLocalData(prev => prev.filter((item) => item.id !== itemToDelete.id));
        toast({ title: "Registro eliminado", status: "success", duration: 3000 });
      } else {
        toast({ title: "Error al borrar", status: "error", duration: 3000 });
      }
      onClose();
    }
  };

  return (
    <Box p={{ base: 2, md: 8 }} bg="gray.50" minH="100vh">
      {/* Header del Dashboard */}
      <Flex mb={8} align="center" direction={{ base: "column", sm: "row" }} gap={4}>
        <Box>
          <Heading size="lg" color="gray.800">{tableName}</Heading>
          <Text color="gray.500" fontSize="sm">Gestioná la información de la plataforma</Text>
        </Box>
        <Spacer />
        <Button
          as={Link}
          to={`/dashboard/${tableName.toLowerCase()}-crear`}
          bg="#E9681F"
          color="white"
          _hover={{ bg: "#d15a1a" }}
          leftIcon={<AddIcon />}
          borderRadius="xl"
          px={8}
        >
          Nuevo Registro
        </Button>
      </Flex>

      {/* Barra de búsqueda con estilo */}
      <Box bg="white" p={4} borderRadius="2xl" shadow="sm" mb={6}>
        <InputGroup size="lg">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder={`Buscar en ${tableName.toLowerCase()}...`}
            borderRadius="xl"
            fontSize="md"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            bg="gray.50"
            border="none"
            _focus={{ bg: "white", ring: 2, ringColor: "blue.400" }}
          />
        </InputGroup>
      </Box>

      {/* Tabla Estilizada */}
      <Box bg="white" shadow="sm" borderRadius="2xl" overflow="hidden" border="1px" borderColor="gray.100">
        <TableContainer>
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                {filterHeaders.map((header, index) => (
                  <Th key={index} color="gray.600" py={5}>{header}</Th>
                ))}
                <Th textAlign="right" pr={10}>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentRows.length > 0 ? (
                currentRows.map((item) => (
                  <Tr key={item.id} _hover={{ bg: "blue.50" }} transition="0.2s">
                    {Object.entries(item)
                      .filter(([key]) => !key.toLowerCase().includes("estado") && !key.toLowerCase().includes("id"))
                      .map(([_, value], idx) => (
                        <Td key={idx} py={5}>
                          {typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value) ? (
                            <Badge variant="subtle" colorScheme="blue" borderRadius="md" px={2}>
                              {new Date(value).toLocaleDateString("es-AR")}
                            </Badge>
                          ) : (
                            <Text fontWeight="medium" color="gray.700">{String(value ?? "—")}</Text>
                          )}
                        </Td>
                      ))}
                    <Td textAlign="right" pr={6}>
                      <Flex justify="flex-end" gap={2}>
                        <Tooltip label="Editar">
                          <IconButton
                            size="sm"
                            aria-label="Editar"
                            icon={<EditIcon />}
                            colorScheme="blue"
                            variant="light"
                            onClick={() => navigate(`/dashboard/${tableName.toLowerCase()}-editar/${item.id}`, { state: item })}
                          />
                        </Tooltip>
                        <Tooltip label="Eliminar">
                          <IconButton
                            size="sm"
                            aria-label="Borrar"
                            icon={<DeleteIcon />}
                            colorScheme="red"
                            variant="light"
                            onClick={() => { setItemToDelete(item); onOpen(); }}
                          />
                        </Tooltip>
                      </Flex>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={filterHeaders.length + 1} textAlign="center" py={20}>
                    <VStack>
                      <SearchIcon boxSize={10} color="gray.300" />
                      <Text color="gray.500">No se encontraron resultados</Text>
                    </VStack>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        <Flex p={6} align="center" bg="gray.50" borderTop="1px" borderColor="gray.100">
          <Text fontSize="sm" color="gray.600">
            Mostrando <b>{indexOfFirstRow + 1}</b> a <b>{Math.min(indexOfLastRow, filteredData.length)}</b> de <b>{filteredData.length}</b> resultados
          </Text>
          <Spacer />
          <Flex gap={2}>
            <IconButton
              size="sm"
              icon={<ArrowLeftIcon w={2} h={2} />}
              onClick={() => setCurrentPage(p => p - 1)}
              isDisabled={currentPage === 1}
              aria-label="Previa"
            />
            <Button size="sm" variant="outline" colorScheme="blue" disabled>
              {currentPage}
            </Button>
            <IconButton
              size="sm"
              icon={<ArrowRightIcon w={2} h={2} />}
              onClick={() => setCurrentPage(p => p + 1)}
              isDisabled={indexOfLastRow >= filteredData.length}
              aria-label="Siguiente"
            />
          </Flex>
        </Flex>
      </Box>

      {/* AlertDialog de Confirmación */}
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
        <AlertDialogOverlay backdropFilter="blur(4px)">
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Confirmar eliminación</AlertDialogHeader>
            <AlertDialogBody>
              ¿Estás seguro de que deseas borrar <strong>{itemToDelete?.[infoColumn]}</strong>? Esta acción no se puede deshacer.
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button ref={cancelRef} onClick={onClose} borderRadius="xl">Cancelar</Button>
              <Button colorScheme="red" onClick={handleDelete} borderRadius="xl" px={8}>Borrar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TableCRUD;